import React, { useEffect, useState } from 'react';
import {
	Alert,
	Button,
	IconButton,
	Tooltip,
	Stack,
	Typography,
	CircularProgress,
	FormGroup,
	FormControlLabel,
	Switch,
	Pagination,
	TableContainer,
	Paper,
	Table,
	TableHead,
	TableCell,
	TableRow,
	TableBody,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { DataGrid, GridToolbar, esES } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
	defaultContactsNumbers,
	isInDevEnv,
	defaultContactsNumbersDev,
	formatterCurrency,
	getFullName,
	handleConfirmModal,
	handleError,
	handleSuccess,
	highestPermissions,
	rowStyle,
	whichStatus,
	whichStatusColor,
	blue_pilu,
	white_pilu,
	formatClientZone,
	formatWord,
	whichEnvURL,
} from '../../helpers/utils';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateMX } from '../../helpers/hours';
import {
	getOrders,
	cancelOrder,
	getStatusCounts,
	getOrdersByStatus,
} from '../../actions/orders';
import { LinearWithValueLabel } from './ProgressBar';
import Label from '../utils/Label';
import { Searcher2 } from '../searcher/Searcher2';
import { loadClients } from '../../actions/catalogs';
import { sendWhatsApp, template1 } from '../../helpers/whatsappApi';
import { inputTextModal } from '../../helpers/sweetAlert2';
import { cleanSearcher, setSearcher } from '../../actions/searcher';

const searchIconStyle = { color: '#143f6c', cursor: 'pointer' };

// Users table
export const OrdersTable3 = ({ quotation = false }) => {
	const defaultEndpoint = `/api/order/?ordering=-created_at&type=${
		quotation ? 'QUO' : 'ORD'
	}`;

	const labelOrderType = quotation ? 'Cotización' : 'Pedido';

	// Hook for navigation
	const navigate = useNavigate();

	// Show searcher or not
	const [showSearcher, setShowSearcher] = useState(false);

	// Status counts
	const [statusCounts, setStatusCounts] = useState(null);

	// Loading state
	const [isLoading, setLoading] = useState(false);

	// Reset rows flag
	const [resetRows, setResetRows] = useState(false);

	// Already used the endpoint in redux
	const [isReduxEndpointUsed, setIsReduxEndpointUsed] = useState(false);

	// Dispatch
	const dispatch = useDispatch();

	// Current user
	const { user, searchConfig: initialStateRows } = useSelector(
		(state) => state
	);
	// Display header
	const [datagridFullView, setDatagridFullView] = useState(true);

	const getCachePage = () => {
		const cachePage = localStorage.getItem('lastTablePage');

		if (cachePage && cachePage?.length > 0 && cachePage !== 'null') {
			return parseInt(cachePage);
		}
		return 1;
	};

	// Dialog table by statuses
	const [open, setOpen] = useState(false);
	const [currentStatusToWatch, setCurrentStatusToWatch] = useState('-');
	const [dialogOrders, setDialogOrders] = useState([]);

	const handleOpen = (currentStatus, statusCode, numberOrders) => {
		setOpen(true);
		setCurrentStatusToWatch(`${currentStatus} (Total: ${numberOrders})`);
		getOrdersByStatus(statusCode).then(({ data }) => {
			// Format data
			const formattedData = data.results.map((item) =>
				createDataObject(item)
			);
			console.log(formattedData);
			setDialogOrders(formattedData);
		});
	};

	const handleClose = () => {
		setOpen(false);
	};

	// Choose endpoint depending on user type
	const whichEndpoint = () => {
		// Use Redux endpoint if it's not empty
		if (initialStateRows?.endpoint?.length > 0) {
			return initialStateRows?.endpoint;
		}

		// Check if the user has high access
		const isAdmin = highestPermissions(user);
		// Page size
		const pageSizeParam = `&page_size=${initialStateRows.pageSize}`;
		// Division filter (if user is not admin)
		const divisionsFilter = isAdmin ? '' : `&division=${user?.division}`;
		// Set page to local storage
		const page = getCachePage();
		// Set page param
		const pageParam = `&page=${page - 1 === 0 ? 1 : page - 1}`;
		// Default endpoint
		return defaultEndpoint + pageParam + pageSizeParam + divisionsFilter;
	};

	// Rows
	const [rowsState, setRowsState] = useState(initialStateRows);

	const addFilterIfAdmin = (tmpUser) => {
		if (highestPermissions(tmpUser)) {
			return '';
		}
		return `&division=${tmpUser?.division}`;
	};

	// Can cancell?
	const canCancell = (row) => {
		return (
			user?.area?.id === 1 || // Mesa de control
			user?.user_type?.id === 2 || // Administrador
			(user?.user_type?.id === 5 && // Asesor
				['ICF', 'VAL'].includes(row.original.status))
		);
	};

	// Format final datagrid object
	const clientTypeMap = {
		CLI1: 'Nuevo',
		CLI2: 'Recuperado',
		CLI3: 'Cautivo',
	};
	const createDataObject = (item) => ({
		...item,
		client_type:
			item?.client_type?.length > 0
				? item?.client_type
				: 'Falta por asignar',
		type_of_client: clientTypeMap[item?.type_of_client] ?? '-',
		zone: formatClientZone(
			item.client_city,
			item.client_municipality,
			item.client_state
		),
		client_city: item.client_city?.length
			? formatWord(item.client_city)
			: '-',
		original: item,
		type: item.type === 'QUO' ? 'Cotización' : 'Orden',
		amount:
			// Mesa de Control, Recepción y Cobranza
			user?.user_type?.id === 4 && ![1, 24, 25].includes(user?.area?.id)
				? '$ ----'
				: `${formatterCurrency(item.amount)}`,
		entry_date: formatDateMX(item.entry_date),
		delivery_date: formatDateMX(item.delivery_date),
		authorization_date: formatDateMX(item.authorization_date),
		validation_date: formatDateMX(item.validation_date),
		status: {
			status: whichStatus(item.status),
			color: whichStatusColor(item.status),
		},
		delivery_forecast: formatDateMX(item.delivery_forecast),
		created_at: formatDateMX(item.created_at),
	});

	// Update row config (local & redux)
	const updateRows = (newConfig, reset = false) => {
		if (reset) {
			setRowsState({
				isLoading: false,
				data: [],
				total: 0,
				page: 1,
				lastPage: 1,
				pageSize: 20,
				next: null,
				previous: null,
				endpoint: null,
				searchActive: false,
			});
			dispatch(cleanSearcher());
		} else {
			// Local
			setRowsState(newConfig);
			// Redux
			dispatch(setSearcher(newConfig));
		}
	};

	const editEndpoint = (endpoint, sum = 0) => {
		let endpointTmp = endpoint.split('page=');
		let endpointTmp2 = endpointTmp[1].split('&');
		endpointTmp2.shift();
		const newEndpoint =
			endpointTmp[0] +
			`page=${rowsState.page + sum}&` +
			endpointTmp2.join('&');
		return newEndpoint;
	};

	const getEndpoint = (moveForward, reset) => {
		let endpoint = null;
		if (reset || !rowsState) {
			endpoint = whichEndpoint();
		} else {
			if (rowsState.endpoint?.length > 0 && !isReduxEndpointUsed) {
				endpoint = rowsState.endpoint;
			} else {
				endpoint = moveForward ? rowsState.next : rowsState.previous;

				if (endpoint === null && rowsState.endpoint) {
					endpoint = rowsState.endpoint;
				} else if (!rowsState.endpoint) {
					endpoint = whichEndpoint();
				} else {
				}
				endpoint = editEndpoint(endpoint);
			}
		}

		return endpoint;
	};

	// Generate rows by array
	const generateRows = async (
		searchEndpoint = null,
		reset = false,
		config = null
	) => {
		// Set variables
		let newOrders = [];
		let total = 0;
		let data = null;
		let newConfig = null;
		let rowsStateTmp = rowsState;

		if (config) {
			rowsStateTmp = config;
		}

		// Start loading
		setLoading(true);

		// Define if it moves forwards or backwards
		const moveForward = rowsStateTmp.page >= rowsStateTmp.lastPage;

		// Get corresponding endpoint
		let endpoint = '';

		if (searchEndpoint) {
			endpoint = searchEndpoint;
		} else {
			endpoint = getEndpoint(moveForward, reset);
		}

		if (endpoint.includes('.mx')) {
			endpoint = endpoint.split('.mx')[1];
		}

		// Get data from endpoint
		data = (await getOrders(searchEndpoint || endpoint)).data;
		setIsReduxEndpointUsed(true);
		// console.log(data);
		// Set data
		newOrders = data.results;
		// Set total
		total = data.count;

		if (newOrders?.length === 0) {
			// Set new config
			newConfig = {
				...rowsStateTmp,
				isLoading: false,
				data: [],
				total: 0,
			};
		} else {
			// Format data
			const formattedData = newOrders.map((item) =>
				createDataObject(item)
			);

			// Set page to local storage
			const cachePage = getCachePage();
			// Set new config
			newConfig = {
				...rowsStateTmp,
				isLoading: false,
				data: formattedData,
				total: total,
				page: cachePage,
				pageSize: rowsStateTmp.pageSize,
			};
		}

		// Update rows (local & redux)
		updateRows({
			...newConfig,
			endpoint: endpoint,
			next: data?.next,
			previous: data?.previous,
			searchActive: showSearcher,
		});

		// Remove loading
		setLoading(false);
	};

	useEffect(() => {
		// Get data
		generateRows(null, resetRows);
	}, [rowsState.page, rowsState.pageSize, resetRows]);

	// Check if search is active and datagrid full view
	useEffect(() => {
		const searchConfig = localStorage.getItem('searchConfigPilu');
		setShowSearcher(!(searchConfig === 'null'));

		const datagridFullViewPilu = localStorage.getItem(
			'datagridFullViewPilu'
		);
		setDatagridFullView(datagridFullViewPilu === 'true');

		getStatusCounts().then(({ data }) =>
			setStatusCounts(data.status_counts)
		);
	}, []);

	// Handle page change actions
	const handlePageChange = (newPage) => {
		const newLastTablePage = newPage + 1;
		const newLastPage = newLastTablePage - 1;
		// Set page to local storage
		localStorage.setItem(
			'lastTablePage',
			newLastTablePage ? newLastTablePage : 1
		);

		const newConfig = {
			...rowsState,
			page: newLastTablePage,
			lastPage: newLastPage > 0 ? newLastPage : 1,
		};
		updateRows(newConfig);
	};

	// Handle delete user with modal confirmation
	const handleDelete = (rowOrder, orderType) => {
		handleConfirmModal(
			'Precaución',
			`¿Seguro que deseas cancelar la ${orderType}?`,
			'warning',
			'Sí, cancelar'
		)
			.then(({ isConfirmed }) => {
				if (isConfirmed) {
					inputTextModal(
						`Cancelar pedido "${rowOrder.number_order}"`,
						'Escriba la razón por la que va a cancelar el pedido',
						''
					)
						.then(({ isConfirmed, value }) => {
							if (isConfirmed) {
								console.log(rowOrder);
								cancelOrder(rowOrder)
									.then(() => {
										sendWhatsApp(
											rowOrder,
											user,
											isInDevEnv()
												? defaultContactsNumbersDev
												: defaultContactsNumbers,
											template1(
												'SISTEMA',
												'SUPERADMIN',
												rowOrder.number_order,
												`El usuario ${getFullName(
													user
												)} canceló la orden porque "${value}".`
											)
										);
										generateRows();
										handleSuccess(
											'Pedido cancelado correctamente.'
										);
									})
									.catch((error) => {
										handleError(
											error,
											'No se pudo cancelar el pedido.'
										);
									});
							}
						})
						.catch((error) => handleError(error));
				}
			})
			.catch((error) => handleError(error));
	};

	// Column style
	const columnStyleCell = {
		headerClassName: 'column-header-pilu',
		headerAlign: 'center',
	};

	// Table row style
	const tableRowStyle = {
		cursor: 'pointer',
	};

	// Define columns
	const columns = [
		{
			field: 'col11',
			...columnStyleCell,
			headerName: 'Acciones',
			width: 140,
			// Add actions to each case
			renderCell: ({ row }) => (
				<>
					{!row.canceled && (
						<Tooltip title='Ver'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => {
									const path = `/dashboard/order/${row.id}`;
									localStorage.setItem('lastPathPilu', path);
									navigate(path);
								}}
							>
								<InfoIcon />
							</IconButton>
						</Tooltip>
					)}
					{/* Mesa de control or Asesor */}
					{(user?.area?.id === 1 ||
						[1, 2, 3, 5].includes(user?.user_type?.id)) &&
						!row.canceled && (
							<>
								<Tooltip title='Editar'>
									<IconButton
										style={{ color: '#143f6c' }}
										aria-label='edit'
										color='primary'
										onClick={() => {
											const path = `/dashboard/editar-orden/${row.id}`;
											localStorage.setItem(
												'lastPathPilu',
												path
											);
											navigate(path);
										}}
									>
										<EditIcon />
									</IconButton>
								</Tooltip>
								{/* Mesa de control o Administradores pueden cancelar */}
								{canCancell(row) && (
									<Tooltip title='Cancelar'>
										<IconButton
											style={{ color: '#143f6c' }}
											aria-label='delete'
											color='primary'
											onClick={() => {
												handleDelete(
													row.original,
													row.type.toLowerCase()
												);
											}}
										>
											<DoDisturbAltIcon />
										</IconButton>
									</Tooltip>
								)}
							</>
						)}
				</>
			),
		},
		{
			field: 'number_order',
			...columnStyleCell,
			headerName: 'Número de pedido',
			width: 140,
			renderCell: ({ row }) => (
				<Tooltip title={row.number_order}>
					<span className='table-cell-trucate'>
						{row.number_order}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'biases_count',
			...columnStyleCell,
			headerName: 'Número de partidas',
			width: 140,
			renderCell: ({ row }) => (
				<Tooltip title={row.biases_count}>
					<span className='table-cell-trucate'>
						{row.biases_count}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'company_name',
			...columnStyleCell,
			headerName: 'Cliente',
			width: 200,
			renderCell: ({ row }) => (
				<Tooltip title={row.company_name}>
					<span className='table-cell-trucate'>
						{row.company_name}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'client_type',
			...columnStyleCell,
			headerName: 'Tipo de cliente (original)',
			width: 230,
			renderCell: ({ row }) => (
				<Tooltip title={row?.client_type}>
					<span className='table-cell-trucate'>
						{row?.client_type}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'type_of_client',
			...columnStyleCell,
			headerName: 'Tipo de cliente (pedido)',
			width: 230,
			renderCell: ({ row }) => (
				<Tooltip title={row?.type_of_client}>
					<span className='table-cell-trucate'>
						{row?.type_of_client}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'zone',
			...columnStyleCell,
			headerName: 'Zona',
			width: 230,
			renderCell: ({ row }) => (
				<Tooltip title={row.zone}>
					<span className='table-cell-trucate'>
						{row.client_city}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'division',
			...columnStyleCell,
			headerName: 'División',
			width: 100,
			renderCell: ({ row }) => (
				<Tooltip title={row.division}>
					<span className='table-cell-trucate'>{row.division}</span>
				</Tooltip>
			),
		},
		{
			field: 'canceled',
			...columnStyleCell,
			headerName: 'Estatus',
			width: 140,
			renderCell: ({ row }) => (
				<Label color={row.canceled ? 'error' : 'success'}>
					{row.canceled ? 'Cancelado' : 'Activo'}
				</Label>
			),
			valueGetter: ({ row }) => (row.canceled ? 'Cancelado' : 'Activo'),
		},
		{
			field: 'status.status',
			...columnStyleCell,
			headerName: 'Estatus',
			width: 140,
			renderCell: ({ row }) => (
				<Label color={row.status.color}>{row.status.status}</Label>
			),
			valueGetter: ({ row }) => row.status.status,
		},
		{
			field: 'amount',
			...columnStyleCell,
			headerName: 'Importe total',
			width: 170,
			renderCell: ({ row }) => (
				<Tooltip title={row.amount}>
					<span className='table-cell-trucate'>{row.amount}</span>
				</Tooltip>
			),
		},
		{
			field: 'progress',
			...columnStyleCell,
			headerName: 'Progreso',
			width: 170,
			renderCell: ({ row }) => (
				<LinearWithValueLabel progress={row.progress} />
			),
			valueGetter: ({ row }) => row.progress,
		},
		{
			field: 'created_at',
			...columnStyleCell,
			headerName: 'Fecha de creación',
			width: 160,
			renderCell: ({ row }) => (
				<Tooltip title={row.created_at}>
					<span className='table-cell-trucate'>{row.created_at}</span>
				</Tooltip>
			),
		},
		{
			field: 'validation_date',
			...columnStyleCell,
			headerName: 'Fecha de validación',
			width: 160,
			renderCell: ({ row }) => (
				<Tooltip title={row.validation_date || 'No asignado'}>
					<span className='table-cell-trucate'>
						{row.validation_date || 'No asignado'}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'authorization_date',
			...columnStyleCell,
			headerName: 'Fecha de autorización',
			width: 160,
			renderCell: ({ row }) => (
				<Tooltip title={row.authorization_date || 'No asignado'}>
					<span className='table-cell-trucate'>
						{row.authorization_date || 'No asignado'}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'delivery_date',
			...columnStyleCell,
			headerName: 'Fecha de entrega',
			width: 160,
			renderCell: ({ row }) => (
				<Tooltip title={row.delivery_date}>
					<span className='table-cell-trucate'>
						{row.delivery_date}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'delivery_forecast',
			...columnStyleCell,
			headerName: 'Fecha de estimada',
			width: 160,
			renderCell: ({ row }) => (
				<Tooltip title={row.delivery_forecast}>
					<span className='table-cell-trucate'>
						{row.delivery_forecast}
					</span>
				</Tooltip>
			),
		},
	];

	const tableCellStyle = {
		color: white_pilu,
	};

	return (
		<div
			style={{
				padding: '20px',
			}}
		>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
				<DialogTitle>
					Pedidos - {currentStatusToWatch}
					<br />
					<small>Solo se pueden mostrar máximo 60 pedidos</small>
				</DialogTitle>

				<DialogContent>
					<div style={{ height: 460, width: '100%' }}>
						<DataGrid
							rows={dialogOrders}
							pagination
							density='compact'
							autoHeight
							pageSize={10}
							columns={columns}
							components={{ Toolbar: GridToolbar }}
							localeText={
								esES.components.MuiDataGrid.defaultProps
									.localeText
							}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color='primary'>
						Cerrar
					</Button>
				</DialogActions>
			</Dialog>

			{datagridFullView && (
				<>
					<Stack
						direction='row'
						alignItems='flex-end'
						justifyContent='space-between'
						style={{
							border: '1px solid white',
							padding: '20px 20px 0 0',
							margin: '0 0 40px 0',
							width: '100%',
						}}
					>
						<Typography variant='h4' gutterBottom>
							{quotation ? 'Cotizaciones' : 'Pedidos'}
						</Typography>
						{/* Mesa de control or Asesor */}
						{(user?.area?.id === 1 ||
							[1, 2, 3, 5].includes(user?.user_type?.id)) && (
							<Button
								variant='contained'
								style={{
									backgroundColor: '#143f6c',
								}}
								onClick={() => {
									dispatch(loadClients(700));
									navigate(
										`/dashboard/nueva-orden/${labelOrderType}`
									);
								}}
							>
								{labelOrderType === 'Cotización'
									? 'Nueva Cotización'
									: 'Nuevo Pedido'}
							</Button>
						)}
					</Stack>

					<hr />
				</>
			)}

			<Typography
				variant='h6'
				gutterBottom
				style={{ ...rowStyle, margin: '20px 0 10px 0' }}
			>
				Búsqueda global{' '}
				<div
					onClick={() => setShowSearcher(!showSearcher)}
					style={{ margin: '0 10px' }}
				>
					{showSearcher ? (
						<SearchOffIcon style={searchIconStyle} />
					) : (
						<SearchIcon style={searchIconStyle} />
					)}
				</div>
				<div style={{ marginLeft: '25px' }}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									checked={datagridFullView}
									onChange={() => {
										setDatagridFullView(!datagridFullView);
										localStorage.setItem(
											'datagridFullViewPilu',
											!datagridFullView
										);
									}}
								/>
							}
							label='Tabla completa'
						/>
					</FormGroup>
				</div>
			</Typography>

			{/* Order searcher */}
			{showSearcher && (
				<Searcher2
					quotation={quotation}
					generateRows={generateRows}
					handleClose={() => setShowSearcher(false)}
					rowsState={rowsState}
					setRowsState={setRowsState}
					whichEndpoint={whichEndpoint}
					addFilterIfAdmin={addFilterIfAdmin}
					initialStateRows={initialStateRows}
					updateRows={updateRows}
					setResetRows={setResetRows}
					editEndpoint={editEndpoint}
				/>
			)}

			{isLoading ? (
				<center style={{ padding: '20%' }}>
					<CircularProgress />
				</center>
			) : (
				<>
					{statusCounts && (
						<TableContainer
							component={Paper}
							style={{ marginBottom: '20px' }}
						>
							<Table>
								<TableHead
									style={{
										backgroundColor: blue_pilu,
									}}
								>
									<TableRow>
										<TableCell style={tableCellStyle}>
											No empezado
										</TableCell>
										<TableCell style={tableCellStyle}>
											Pedidos en configuración
										</TableCell>
										<TableCell style={tableCellStyle}>
											Pedidos validados
										</TableCell>
										<TableCell style={tableCellStyle}>
											Pedidos autorizados
										</TableCell>
										<TableCell style={tableCellStyle}>
											Pedidos liberados
										</TableCell>
										<TableCell style={tableCellStyle}>
											En progreso
										</TableCell>
										<TableCell style={tableCellStyle}>
											Completados
										</TableCell>
										<TableCell style={tableCellStyle}>
											Pedidos atrasados
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<TableRow>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'No empezado',
													'NST',
													statusCounts?.NST
												)
											}
										>
											{statusCounts?.NST}
										</TableCell>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'Pedidos en configuración',
													'ICF',
													statusCounts?.ICF
												)
											}
										>
											{statusCounts?.ICF}
										</TableCell>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'Pedidos validados',
													'VAL',
													statusCounts?.VAL
												)
											}
										>
											{statusCounts?.VAL}
										</TableCell>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'Pedidos autorizados',
													'AUT',
													statusCounts?.AUT
												)
											}
										>
											{statusCounts?.AUT}
										</TableCell>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'Pedidos liberados',
													'REL',
													statusCounts?.REL
												)
											}
										>
											{statusCounts?.REL}
										</TableCell>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'En progreso',
													'IPR',
													statusCounts?.IPR
												)
											}
										>
											{statusCounts?.IPR}
										</TableCell>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'Completados',
													'FIN',
													statusCounts?.FIN
												)
											}
										>
											{statusCounts?.FIN}
										</TableCell>
										<TableCell
											style={tableRowStyle}
											onClick={() =>
												handleOpen(
													'Pedidos atrasados',
													'DLY',
													statusCounts?.DLY
												)
											}
										>
											{statusCounts?.DLY}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					)}

					{/* Datagrid table */}
					{rowsState.data.length > 0 ? (
						<div>
							<DataGrid
								rows={rowsState.data}
								rowCount={rowsState.total}
								loading={rowsState.isLoading}
								pagination
								density='compact'
								rowsPerPageOptions={[20, 30, 50, 70, 100]}
								autoHeight
								page={rowsState.page - 1}
								pageSize={rowsState.pageSize}
								pageSizeOptions={[20]}
								paginationMode='server'
								onPageChange={(newPage) =>
									handlePageChange(newPage)
								}
								onPageSizeChange={(newPage) => {
									let newConfig = null;
									if (showSearcher) {
										newConfig = {
											...rowsState,
											pageSize: newPage,
											next: `${
												rowsState.current
											}&page_size=${newPage}${addFilterIfAdmin(
												user
											)}`,
											page: 1,
											lastPage: 1,
										};
									} else {
										newConfig = {
											...rowsState,
											pageSize: newPage,
											next: `/api/order/?ordering=-created_at&type=${
												quotation ? 'QUO' : 'ORD'
											}&canceled=0&page_size=${newPage}${addFilterIfAdmin(
												user
											)}`,
											page: 1,
											lastPage: 1,
										};
									}

									updateRows(newConfig);
								}}
								columns={columns}
								// components={{ Toolbar: GridToolbar }}
								localeText={
									esES.components.MuiDataGrid.defaultProps
										.localeText
								}
							/>
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'end',
									justifyContent: 'flex-end',
								}}
							>
								<Pagination
									count={Math.ceil(
										rowsState.total / rowsState.pageSize
									)}
									page={rowsState.page}
									onChange={({ target }) => {
										const newPage =
											parseInt(target.innerText) - 1;
										handlePageChange(newPage);
									}}
									size='large'
									hidePrevButton
									hideNextButton
								/>
							</div>
						</div>
					) : (
						<div style={{ margin: '30px 0' }}>
							<Alert severity='info'>
								No hay ninguna {labelOrderType.toLowerCase()}
							</Alert>
						</div>
					)}
				</>
			)}
		</div>
	);
};
